import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';

// Web Speech API voice input. Supports Hindi + English + Hinglish.
// Parses simple utterances like:
//  "Mumbai to Delhi 20 tonnes cement 80000 rupees advance 50 percent"
//  "मुंबई से दिल्ली बीस टन सीमेंट असी हज़ार रुपये"
export default function VoiceInput({ onResult, cities = [], materials = [], lang = 'en-IN' }) {
  const [listening, setListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [supported, setSupported] = useState(true);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setSupported(false); return; }
    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = lang;
    rec.maxAlternatives = 1;
    rec.onresult = (e) => {
      let text = '';
      for (let i = e.resultIndex; i < e.results.length; i++) text += e.results[i][0].transcript;
      setTranscript(text);
      if (e.results[e.results.length - 1].isFinal) {
        parseAndFill(text);
        setListening(false);
      }
    };
    rec.onerror = (e) => {
      setListening(false);
      toast({ title: 'Voice input error', description: e.error === 'not-allowed' ? 'Please allow microphone access' : e.error });
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    return () => rec.abort();
  }, [lang]);

  // Hindi number words to digits
  const hindiNums = {
    'एक': 1, 'दो': 2, 'तीन': 3, 'चार': 4, 'पांच': 5, 'छह': 6, 'सात': 7, 'आठ': 8, 'नौ': 9, 'दस': 10,
    'ग्यारह': 11, 'बारह': 12, 'पंद्रह': 15, 'बीस': 20, 'पचिस': 25, 'तीस': 30, 'चालीस': 40, 'पचास': 50,
    'साठ': 60, 'सत्तर': 70, 'असी': 80, 'नब्बे': 90, 'सौ': 100, 'हज़ार': 1000, 'लाख': 100000,
  };

  const wordsToNum = (str) => {
    // Convert "eighty thousand" or "assi hazar" style to numbers
    let s = str.toLowerCase();
    // English number words
    const en = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, fifteen: 15, twenty: 20, thirty: 30, forty: 40, fifty: 50, sixty: 60, seventy: 70, eighty: 80, ninety: 90, hundred: 100, thousand: 1000, lakh: 100000 };
    Object.entries(en).forEach(([w, n]) => { s = s.replace(new RegExp('\\b' + w + '\\b', 'g'), ' ' + n + ' '); });
    Object.entries(hindiNums).forEach(([w, n]) => { s = s.split(w).join(' ' + n + ' '); });
    // Compose: "80 1000" => 80000, "1 100000" => 100000
    const parts = s.match(/[\d.]+/g) || [];
    if (parts.length === 0) return null;
    let total = 0, current = 0;
    parts.forEach(p => {
      const n = parseFloat(p);
      if (n === 100 || n === 1000 || n === 100000) {
        current = (current || 1) * n;
        total += current;
        current = 0;
      } else {
        current += n;
      }
    });
    total += current;
    return total;
  };

  const parseAndFill = (text) => {
    const lower = text.toLowerCase();
    const result = { raw: text };

    // Find cities (English name match)
    const cityMatches = [];
    cities.forEach(c => {
      const name = c.split(',')[0].toLowerCase();
      if (lower.includes(name)) cityMatches.push({ city: c, idx: lower.indexOf(name) });
    });
    cityMatches.sort((a, b) => a.idx - b.idx);
    if (cityMatches[0]) result.origin = cityMatches[0].city;
    if (cityMatches[1]) result.destination = cityMatches[1].city;

    // Material
    materials.forEach(m => { if (lower.includes(m.toLowerCase())) result.material = m; });

    // Weight in tonnes
    const wt = lower.match(/(\d+(?:\.\d+)?)\s*(?:t|ton|tonne|tonnes|टन|tan)/);
    if (wt) result.weight = wt[1];

    // Rate: any "rupees" "rs" "रुपये" number
    const rateContext = lower.replace(/(mumbai|delhi|bengaluru|chennai|kolkata|hyderabad|ahmedabad|pune|surat|jaipur|lucknow|kanpur|nagpur|indore|bhopal|coimbatore|vadodara|ludhiana|agra|nashik)/g, '');
    const num = wordsToNum(rateContext);
    if (num && num > 500) result.rate = String(Math.round(num));

    onResult(result);
  };

  const toggle = () => {
    if (!supported) { toast({ title: 'Voice not supported', description: 'Please use Chrome or Edge browser.' }); return; }
    if (listening) {
      recognitionRef.current?.stop();
    } else {
      setTranscript('');
      recognitionRef.current?.start();
      setListening(true);
      toast({ title: 'Listening...', description: lang === 'hi-IN' ? 'बोलें: मुंबई से दिल्ली 20 टन सीमेंट 80,000 रुपये' : 'Say: Mumbai to Delhi 20 tonnes cement 80000 rupees' });
    }
  };

  if (!supported) return null;

  return (
    <div className="flex items-center gap-2">
      <Button type="button" onClick={toggle} size="sm" variant={listening ? 'default' : 'outline'} className={listening ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' : ''}>
        {listening ? <><MicOff className="w-4 h-4 mr-1" /> Stop</> : <><Mic className="w-4 h-4 mr-1" /> Voice Fill</>}
      </Button>
      {transcript && <span className="text-xs text-gray-500 italic truncate max-w-[240px]">“{transcript}”</span>}
    </div>
  );
}
